import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "src/components/layout/website/Layout";
import Alert from "src/components/ui/Alert";
import PlanPriceCard from "src/components/cards/PlanPriceCard";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as planActions from "src/redux/slices/planSlice";
import { PAGE_SEO } from "src/constants/seo";
import { trackCta, trackEvent } from "src/utils/analytics";

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: plans, loading, error } = useAppSelector(
    (state) => state.plan.publicPlansList
  );

  useEffect(() => {
    dispatch(planActions.getPublicPlanListRequest());
  }, [dispatch]);

  return (
    <Layout meta={PAGE_SEO.pricing}>
      <div className="min-h-screen bg-gray-50 py-20 px-6 text-center">
        <h1 className="text-4xl font-bold text-indigo-600 mb-4">
          Pricing Plans
        </h1>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          We're in early access — pricing and limits below are current, but
          may evolve as we learn from the first businesses on the platform.
        </p>

        <div className="mb-12 mx-auto max-w-3xl rounded-2xl border border-indigo-100 bg-indigo-50 px-6 py-5 text-center dark:border-indigo-900 dark:bg-indigo-950">
          <p className="text-base font-semibold text-indigo-900 dark:text-indigo-200">
            Not ready to commit? Start with a 15-day free trial
          </p>
          <p className="mt-1 text-sm text-indigo-700 dark:text-indigo-400">
            1 user, 100 products — no card required, usually reviewed within
            one business day.
          </p>
          <button
            onClick={() => {
              trackCta("Start Free Trial", "pricing_trial_banner");
              navigate("/app/register");
            }}
            className="mt-4 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Start Free Trial
          </button>
        </div>

        <div className="max-w-6xl mx-auto">
          {loading && <p className="text-gray-500">Loading plans…</p>}

          {error && (
            <div className="max-w-md mx-auto">
              <Alert type="error" message={error} />
            </div>
          )}

          {!loading && !error && plans && plans.length === 0 && (
            <p className="text-gray-500">
              Plans aren't published yet. Contact us for early access.
            </p>
          )}

          {!loading && !error && plans && plans.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {plans.map((p) => (
                <PlanPriceCard
                  key={p.planCode}
                  plan={p}
                  ctaLabel={p.price ? "Talk to Us" : "Start Free"}
                  onCtaClick={() => {
                    trackEvent("plan_cta_click", {
                      plan_code: p.planCode,
                      plan_name: p.planName,
                    });
                    navigate("/contact");
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
